import {
  ExcelIngestProgressList,
  type ProgressEntry,
} from '@/components/admin/data-sources/ExcelIngestProgressList';
import {
  ExcelSheetMappingTable,
  type MappingRow,
  buildAppendBodyFromRows,
} from '@/components/admin/data-sources/ExcelSheetMappingTable';
import { PageHeader } from '@/components/layout/PageHeader';
import { PageShell } from '@/components/layout/PageShell';
import { Button } from '@/components/ui/button';
import { ApiError, api } from '@/lib/api';
import { streamExcelAppend, streamExcelAppendParse } from '@/lib/excelDataSourceStream';
import type { AppendExcelParseResult } from '@innermate/shared';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { ArrowLeft, FileSpreadsheet, Loader2, Upload } from 'lucide-react';
import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { FileDropZone } from './NewExcel';

type Step = 'upload' | 'parsing' | 'mapping' | 'applying' | 'failed';

const SUPPORTED_EXTENSIONS = ['.xlsx', '.xls', '.xlsm', '.xlsb', '.csv', '.psv', '.sam'];

interface Props {
  id: string;
}

export function AdminDataSourceAppendExcel({ id }: Props) {
  const navigate = useNavigate();
  const fileInputId = useId();

  const [step, setStep] = useState<Step>('upload');
  const [file, setFile] = useState<File | null>(null);
  const [fileUploadId, setFileUploadId] = useState<string | null>(null);
  const [parseResult, setParseResult] = useState<AppendExcelParseResult | null>(null);
  const [rows, setRows] = useState<MappingRow[]>([]);
  const [entries, setEntries] = useState<ProgressEntry[]>([]);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const dataSourceQuery = useQuery({
    queryKey: ['admin', 'data-sources', id],
    queryFn: () => api.dataSourceGet(id),
  });
  const tablesQuery = useQuery({
    queryKey: ['admin', 'data-sources', id, 'tables'],
    queryFn: () => api.dataSourceTableList(id),
  });

  // 기존 테이블 목록 — sheetName 매핑은 백엔드 autoMapping 가 책임지지만,
  // existing dropdown 에서 보여줄 매칭은 (id, sheetName, tableName) 모두 필요.
  // sheetName 은 data_source_excel_meta 에 있고 tables endpoint 는 그걸 직접 노출하지 않으므로,
  // autoMapping 의 (newSheetName, targetTableId) 와 전체 tables 목록을 결합해서 보완.
  const tableInfos = useMemo(() => {
    const tables = tablesQuery.data?.tables ?? [];
    if (!parseResult) {
      return tables.map((t) => ({ id: t.id, sheetName: t.tableName, tableName: t.tableName }));
    }
    const sheetByTableId = new Map<string, string>();
    for (const m of parseResult.autoMapping) {
      if (m.targetTableId) sheetByTableId.set(m.targetTableId, m.newSheetName);
    }
    return tables.map((t) => ({
      id: t.id,
      tableName: t.tableName,
      sheetName: sheetByTableId.get(t.id) ?? t.tableName,
    }));
  }, [tablesQuery.data, parseResult]);

  const upsertEntry = useCallback((entry: Omit<ProgressEntry, 'id'> & { id?: string }) => {
    setEntries((prev) => {
      if (entry.dedupKey) {
        const idx = prev.findIndex((e) => e.dedupKey === entry.dedupKey);
        if (idx >= 0) {
          const next = prev.slice();
          const cur = next[idx];
          if (cur) {
            next[idx] = {
              ...cur,
              kind: entry.kind,
              text: entry.text,
              endedAt: entry.endedAt ?? cur.endedAt,
              startedAt: entry.startedAt ?? cur.startedAt,
            };
          }
          return next;
        }
      }
      const id =
        entry.id ?? `${Date.now()}-${prev.length}-${Math.random().toString(36).slice(2, 6)}`;
      return [...prev, { ...entry, id }];
    });
  }, []);

  const handleFile = (f: File | null) => {
    setFile(f);
    setFileUploadId(null);
    setParseResult(null);
    setRows([]);
    setEntries([]);
    setErrorMsg(null);
  };

  const onDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragOver(false);
      const dropped = e.dataTransfer.files?.[0];
      if (!dropped) return;
      const lower = dropped.name.toLowerCase();
      if (!SUPPORTED_EXTENSIONS.some((ext) => lower.endsWith(ext))) {
        setErrorMsg(`지원하지 않는 확장자입니다: ${dropped.name}`);
        return;
      }
      handleFile(dropped);
    },
    [handleFile],
  );

  const handleStartParse = async () => {
    if (!file) return;
    setErrorMsg(null);
    setStep('parsing');
    setEntries([]);
    upsertEntry({ kind: 'progress', text: '파일 업로드 중…' });
    try {
      const uploaded = await api.fileUpload(file);
      setFileUploadId(uploaded.id);
      upsertEntry({ kind: 'progress', text: `업로드 완료: ${uploaded.originalName}` });
      const result = await streamExcelAppendParse(id, uploaded.id, {
        onProgress: (m) => upsertEntry({ kind: 'progress', text: m }),
        onSheetStart: (s) =>
          upsertEntry({
            dedupKey: `parse:${s}`,
            kind: 'sheet_in_progress',
            text: `시트 처리 중: ${s}`,
            startedAt: Date.now(),
          }),
        onSheetDone: ({ sheetName }) =>
          upsertEntry({
            dedupKey: `parse:${sheetName}`,
            kind: 'sheet_done',
            text: `시트 처리 완료: ${sheetName}`,
            endedAt: Date.now(),
          }),
      });
      if (!result.parse) {
        throw new Error('파싱 결과를 받지 못했습니다.');
      }
      setParseResult(result.parse);
      setStep('mapping');
      upsertEntry({
        kind: 'ingest_done',
        text: `파싱 완료 — 시트 ${result.parse.sheets.length} 개 (자동 매핑: ${result.parse.autoMapping.filter((m) => m.matchKind === 'exact').length})`,
      });
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : (err as Error).message;
      setErrorMsg(msg);
      upsertEntry({ kind: 'error', text: msg });
      setStep('failed');
    }
  };

  // parseResult 로 매핑 행 초기화.
  useEffect(() => {
    if (!parseResult) return;
    setRows(
      parseResult.sheets.map((s) => ({
        newSheetName: s.sheetName,
        mode: 'auto',
        selectedTableId: null,
        newTableConfig: null,
      })),
    );
  }, [parseResult]);

  const validation = useMemo(() => {
    if (!parseResult) return null;
    for (const r of rows) {
      if (r.mode === 'existing' && !r.selectedTableId) {
        return `대상 테이블을 선택하세요 (${r.newSheetName}).`;
      }
      if (r.mode === 'new' && !r.newTableConfig) {
        return `신규 테이블 컬럼이 미설정입니다 (${r.newSheetName}).`;
      }
      if (r.mode === 'new' && r.newTableConfig) {
        const cfg = r.newTableConfig;
        if (cfg.tableName.trim().length === 0) {
          return `신규 테이블명이 비었습니다 (${r.newSheetName}).`;
        }
        for (const c of cfg.columns) {
          if (c.label.trim().length === 0) {
            return `신규 시트 컬럼 라벨이 비었습니다 (${r.newSheetName} → ${c.sqlName}).`;
          }
        }
      }
    }
    return null;
  }, [parseResult, rows]);

  const handleApply = async () => {
    if (!fileUploadId) return;
    if (validation) {
      setErrorMsg(validation);
      return;
    }
    setErrorMsg(null);

    // 신규 테이블 생성 (createNewTable=true) 시트들은 등록 직전 PK 중복 자동 검사.
    // 기존 테이블 매핑 (existing mode) 은 영구 테이블의 ON CONFLICT 로 처리되므로 skip.
    const newTableSheets = rows.filter(
      (r) => r.mode === 'new' && r.newTableConfig && r.newTableConfig.pkColumns.length > 0,
    );
    if (newTableSheets.length > 0) {
      upsertEntry({
        kind: 'progress',
        text: `PK 중복 검사 중… (신규 테이블 ${newTableSheets.length} 시트)`,
      });
      try {
        for (const r of newTableSheets) {
          const cfg = r.newTableConfig;
          if (!cfg) continue;
          const pkParseColumns = cfg.pkColumns
            .map((sqlName) => cfg.columns.find((c) => c.sqlName === sqlName)?.parseSqlName)
            .filter((v): v is string => typeof v === 'string');
          if (pkParseColumns.length === 0) continue;
          const result = await api.dataSourceExcelCheckPkDuplicates({
            fileUploadId,
            sheetName: r.newSheetName,
            pkParseColumns,
          });
          if (result.hasDuplicates) {
            const sample = result.samples
              .slice(0, 3)
              .map((x) => `(${x.values.map((v) => v ?? 'NULL').join(', ')}) × ${x.count}`)
              .join(', ');
            const msg = `시트 "${r.newSheetName}" 의 PK 컬럼 (${cfg.pkColumns.join(', ')}) 에 중복이 있어 등록할 수 없습니다 — ${result.totalDuplicateGroups.toLocaleString()} 그룹 (예: ${sample}). PK 를 해제하거나 데이터를 정리한 뒤 다시 시도하세요.`;
            setErrorMsg(msg);
            upsertEntry({ kind: 'ingest_error', text: msg });
            setStep('failed');
            return;
          }
          upsertEntry({
            kind: 'ingest_done',
            text: `${r.newSheetName}: PK 중복 없음`,
          });
        }
      } catch (err) {
        const msg = err instanceof ApiError ? err.message : (err as Error).message;
        setErrorMsg(`PK 중복 검사 실패: ${msg}`);
        upsertEntry({ kind: 'error', text: `PK 검사 실패: ${msg}` });
        setStep('failed');
        return;
      }
    }

    setStep('applying');
    upsertEntry({ kind: 'progress', text: '추가 업로드 적용 시작…' });
    try {
      const body = buildAppendBodyFromRows(fileUploadId, rows);
      const result = await streamExcelAppend(id, body, {
        onProgress: (m) => upsertEntry({ kind: 'progress', text: m }),
        onSheetStart: (s) =>
          upsertEntry({
            dedupKey: `apply:${s}`,
            kind: 'sheet_in_progress',
            text: `시트 적용 중: ${s}`,
            startedAt: Date.now(),
          }),
        onIngestSheetDone: (r) => {
          if (r.error) {
            upsertEntry({
              dedupKey: `apply:${r.sheetName}`,
              kind: 'ingest_error',
              text: `${r.sheetName}: ${r.error}`,
              endedAt: Date.now(),
            });
          } else {
            const parts: string[] = [];
            if (r.rowsInserted > 0) parts.push(`+${r.rowsInserted.toLocaleString()} 추가`);
            if (r.rowsUpdated > 0) parts.push(`~${r.rowsUpdated.toLocaleString()} 갱신`);
            if (r.rowsDeleted > 0) parts.push(`−${r.rowsDeleted.toLocaleString()} 삭제`);
            const summary = parts.length > 0 ? parts.join(' · ') : '변경 없음';
            upsertEntry({
              dedupKey: `apply:${r.sheetName}`,
              kind: 'ingest_done',
              text: `${r.sheetName}: ${summary}`,
              endedAt: Date.now(),
            });
          }
        },
      });
      const errorCount = result.sheets.filter((s) => s.error).length;
      if (errorCount === 0) {
        upsertEntry({ kind: 'ingest_done', text: '완료 — 데이터소스 상세로 이동합니다.' });
        navigate({ to: '/admin/data-sources/$id', params: { id } });
      } else {
        upsertEntry({
          kind: 'error',
          text: `${errorCount} 시트에 오류가 있어 마법사에 머무릅니다.`,
        });
        setStep('failed');
      }
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : (err as Error).message;
      setErrorMsg(msg);
      upsertEntry({ kind: 'error', text: msg });
      setStep('failed');
    }
  };

  return (
    <PageShell
      header={
        <>
          <button
            type="button"
            onClick={() => navigate({ to: '/admin/data-sources/$id', params: { id } })}
            className="mb-4 inline-flex items-center gap-1 text-xs text-foreground-muted hover:text-foreground"
          >
            <ArrowLeft className="size-3.5" />
            데이터소스 상세
          </button>
          <PageHeader
            title={`${dataSourceQuery.data?.name ?? '데이터소스'} — 엑셀 추가 업로드`}
            description="새 엑셀을 업로드해 기존 시트에 매핑합니다. 시트명이 같으면 자동, 다르면 수동 매핑."
          />
        </>
      }
    >
      {errorMsg ? (
        <div
          role="alert"
          className="mb-4 rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive"
        >
          {errorMsg}
        </div>
      ) : null}

      {step === 'upload' || step === 'failed' ? (
        <section className="space-y-3 rounded-2xl border border-border bg-surface px-4 py-4">
          <FileDropZone
            file={file}
            isDragOver={isDragOver}
            onClickPick={() => fileInputRef.current?.click()}
            onDragEnter={() => setIsDragOver(true)}
            onDragLeave={() => setIsDragOver(false)}
            onDrop={onDrop}
          />
          <input
            ref={fileInputRef}
            id={fileInputId}
            type="file"
            accept={SUPPORTED_EXTENSIONS.join(',')}
            onChange={(e) => handleFile(e.target.files?.[0] ?? null)}
            className="sr-only"
          />
          <div className="flex items-center justify-end gap-2">
            <Button
              variant="ghost"
              onClick={() => navigate({ to: '/admin/data-sources/$id', params: { id } })}
            >
              취소
            </Button>
            <Button onClick={handleStartParse} disabled={!file}>
              <Upload className="size-3.5" />
              파일 분석 시작
            </Button>
          </div>
        </section>
      ) : null}

      {step === 'parsing' || step === 'applying' ? (
        <section className="space-y-3 rounded-2xl border border-border bg-surface px-4 py-4">
          <div className="flex items-center gap-2 text-sm">
            <Loader2 className="size-4 animate-spin text-accent" />
            {step === 'parsing' ? '파일 분석 중…' : '추가 업로드 적용 중…'}
          </div>
          <ExcelIngestProgressList entries={entries} />
        </section>
      ) : null}

      {step === 'mapping' && parseResult ? (
        <div className="space-y-4">
          <section className="space-y-2 rounded-2xl border border-border bg-surface px-4 py-3">
            <div className="flex items-center gap-2 text-sm">
              <FileSpreadsheet className="size-4 text-accent" />
              시트 매핑 — {parseResult.sheets.length} 개 시트
            </div>
            <ExcelIngestProgressList entries={entries} />
          </section>

          <ExcelSheetMappingTable
            parse={parseResult}
            existingTables={tableInfos}
            rows={rows}
            onChange={setRows}
          />

          <div className="flex items-center justify-between gap-2">
            <p className="text-xs text-foreground-muted">
              {validation
                ? validation
                : `적용 시트: ${rows.filter((r) => r.mode !== 'skip' && (r.mode !== 'auto' || parseResult.autoMapping.find((m) => m.newSheetName === r.newSheetName)?.targetTableId)).length} / ${rows.length}`}
            </p>
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                onClick={() => {
                  setStep('upload');
                  setEntries([]);
                  setParseResult(null);
                }}
              >
                다시 업로드
              </Button>
              <Button onClick={handleApply} disabled={!!validation}>
                적용
              </Button>
            </div>
          </div>
        </div>
      ) : null}
    </PageShell>
  );
}
